import { FC, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { format, parse } from "date-fns";
import Select, { GroupBase, MultiValue, OptionsOrGroups } from "react-select";
import DatePicker from "react-datepicker";
import ReactInputMask from "react-input-mask";

import useFetch from "src/hooks/useFetch";
import HubInputField from "src/components/inputType";

import { APIEndpoints } from "src/types/apiTypes";
import { DiagnosisResponseProps, CaseStepOneProps } from "./types";
import CurrentAndPriorTreatment from "../addCase/currentAndPriorTreatment";
import { NYHA_CLASSES } from "src/constants/cases";
import AfrezzaPrescription from "../afrezzaPrescription";
import ProvidePatientTreatment from "../addCase/providePatientTreatment";




const CaseStepOne: FC<CaseStepOneProps> = ({ control, isVisible, watch, setValue, getValues, resetField,isCloneCase }): JSX.Element => {
  const { data: rfcListing, mutate: getDiagnosis } =
    useFetch<DiagnosisResponseProps>(APIEndpoints.GET_DIAGNOSIS_DROPDOWN);

  const [selectedDiagnosisCodes, setSelectedDiagnosisCodes] = useState<{ value: string; label: string }[]>([]);

  const updateSelectedDiagnosisCodes = (vals: { value: string; label: string }[]) => {
    setSelectedDiagnosisCodes(vals);
  };

  useEffect(() => {
    getDiagnosis(null, null, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div className={!isVisible ? 'd-none' : ''}>
      <div className="mb-3">
        <div className="card mb-3">
          <div className="card-header">
            <h1 className="m-0">Getting Started</h1>
          </div>

          <div className="card-body">
            <p>
              In order to process your request for benefit investigation, the following information must be completed. All information is
              kept confidential according to HIPAA guidelines. Please fill out the fields below as completely as possible. Missing fields
              may result in a delay to processing patient benefits in a timely manner.
            </p>


            <div className="row">
               <div className="col-lg-6">
                <Controller
                  name="prescription_attributes.condition"
                  control={control}
                  defaultValue=""
                  render={( { field, fieldState: { error } } ) => (
                    <HubInputField
                      inputId="prescription_attributes.condition"
                      inputLabel="Patient Diagnosis"
                      isRequired={true}
                      isRowBased={true}
                      isRowInsideAnother={true}
                      field={field}
                      error={error}
                      isDropdown={true}
                      isSearchable={false}
                      inputPlaceholder="Select Patient Diagnosis"
                      isTypeAhead
                      dropdownData={
                        NYHA_CLASSES.map( ( item ) => ( { id: item.id, name: item.name } ) ).map(
                          ( procedure ) => ( { value: procedure.id, label: procedure.name } )
                        ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                      }
                    />
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-lg-6">
                <div className="row mb-2">
                  <label htmlFor="prescription_attributes.diagnosis_code" className="form-label col-lg-4 col-md-4 col-12">
                    Diagnosis Code *
                  </label>
                  <div className="col-lg-8 col-md-8 col-12">
                    <Controller
                      name="prescription_attributes.diagnosis_code"
                      control={control}
                      defaultValue={[]}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <Select
                            options={
                              (rfcListing && rfcListing.data && rfcListing.data.length > 0
                                ? rfcListing.data.map((option) => ({ value: option.code, label: option.fullname }))
                                : []) as unknown as OptionsOrGroups<string, GroupBase<string>>
                            }
                            components={{
                              MultiValueLabel: (props) => {
                                const opt = props.data as { value: string; label: string };
                                const found = selectedDiagnosisCodes.find((item) => item.value === opt.value);
                                return (
                                  <div
                                    {...props.innerProps}
                                    className="p-1"
                                    style={{ fontSize: 12 }}
                                    title={found?.label ?? opt.label}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                  >
                                    {found?.value ?? opt.value}
                                  </div>
                                );
                              }
                            }}
                            id="prescription_attributes.diagnosis_code"
                            isSearchable={false}
                            isMulti={true}
                            placeholder="Select diagnosis code"
                            classNames={{ control: () => `input-group ${error?.message ? "border-danger" : ""}` }}
                            {...field}
                            noOptionsMessage={() => "Select Option..."}
                            value={
                              field &&
                              field.value.map((item: string) => ({
                                value: item,
                                label: item,
                              })) as unknown as MultiValue<unknown>
                            }
                            onChange={(val) => {
                              updateSelectedDiagnosisCodes(
                                (val as MultiValue<{ value: string; label: string }>).map(
                                  (item) => ({ value: item.value, label: item.label })
                                )
                              );
                              field.onChange(
                                (val as MultiValue<{ value: string; label: string }>).map((item) => item.value)
                              );
                            }}
                          />
                          {error && error.message ? (
                            <div className="text-danger text-small mt-2">{error.message}</div>
                          ) : null}
                        </>
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <Controller
                  name="prescription_attributes.note"
                  control={control}
                  defaultValue=""
                  render={( { field, fieldState: { error } } ) => (
                    <HubInputField
                      inputId="prescription_attributes.note"
                      isRowBased={true}
                      isRowInsideAnother={true}
                      isRequired={false}
                      field={field}
                      inputLabel="Notes"
                      inputPlaceholder="Note Shared With Patient"
                      error={error}
                    />
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-lg-6">
                <Controller
                  name="prescription_attributes.scheduled_date_of_service"
                  control={control}
                  defaultValue=""
                  render={( { field, fieldState: { error } } ) => (
                    <div className="row align-items-center mb-2">
                      <label htmlFor="patient_attributes.dob"
                        className="col-lg-4 col-md-4 col-12 form-label text-end mb-0 pe-0">
                        Anticipated Date of Service
                      </label>
                      <div className="col-lg-8 col-md-8 col-12 position-relative">
                        <DatePicker
                          selected={
                            field.value
                              ? parse( field.value, "yyyy-MM-dd", new Date() ) // Parse the date from the default format if value exists
                              : null
                          }
                          onChange={( date ) => {
                            field.onChange( date ? format( date, "yyyy-MM-dd" ) : "" ); // Format back to yyyy-MM-dd
                          }}
                          dateFormat="MM/dd/yyyy" // Display format
                          className={`form-control ${error?.message ? "is-invalid" : ""}`}
                          placeholderText="mm/dd/yyyy"
                          showMonthDropdown
                          showYearDropdown
                          customInput={
                            <ReactInputMask mask="99/99/9999">
                              {( inputProps: {} ) => <input {...inputProps} />}
                            </ReactInputMask>
                          }
                          wrapperClassName="w-100"
                          minDate={new Date()} // Restrict past dates
                        />
                        <i
                          className="bi bi-calendar position-absolute"
                          style={{
                            right: error?.message ? "50px" : "25px", // Move left on validation error
                            top: "18px",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                          }}
                        ></i>
                        {error && error.message ? (
                          <div
                            dangerouslySetInnerHTML={{ __html: error.message }}
                            className="text-danger text-small mt-2"
                          ></div>
                        ) : null}
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>

            <AfrezzaPrescription control={control} watch={watch} setValue={setValue} getValues={getValues} resetField={resetField} isCloneCase={isCloneCase}/>

            {/* <Controller
              name="prescription_attributes.quickstart"
              control={control}
              defaultValue={false}
              render={( { field } ) => (
                <div className="row form-check d-flex mb-2">
                    <label className="form-label col-12 col-md-4 col-lg-2"></label>
                    <div className="col-12 col-md-8 col-lg-10">
                        <input
                            className="form-check-input" type="checkbox"
                            id="prescription_attributes.quickstart"
                            disabled checked={field.value}
                        />
                        <label className="form-check-label" htmlFor="prescription_attributes.quickstart">
                            I, the undersigned Prescriber, hereby authorize the delivery of up to two free doses of Afrezza to the patient's
                            address for next-day delivery, if eligible.
                        </label>
                    </div>
                </div>
              )}
            /> */}

            <Controller
              name="prescription_attributes.written_date"
              control={control}
              defaultValue={format( new Date(), 'MM/dd/yyyy' )}
              render={( { field: { value } } ) => (
                <div className="row form-check d-flex">
                    <label className="form-label col-12 col-md-4 col-lg-2"></label>
                    <div className="col-12 col-md-8 col-lg-10">
                      <input className="form-check-input" type="checkbox" id="flexCheckIndeterminateDisabled" disabled checked/>
                      <label className="form-check-label" htmlFor="flexCheckIndeterminateDisabled">
                        Dispense as Written (written date will be {value})
                      </label>
                    </div>
                </div>
              )}
            />

            <Controller
              name="prescription_attributes.drug_name"
              control={control}
              render={() => <></>}
            />
          </div>
        </div>
      </div>

     <div className="card mb-3">
        <div className="card-header">
          <h1 className="m-0">Provide Patient Training</h1>
        </div>
        <div className="card-body">
          <ProvidePatientTreatment control={control} watch={watch} setValue={setValue} getValues={getValues} resetField={resetField} />
        </div>
      </div>
     <div className="card mb-3">
        <div className="card-header">
          <h1 className="m-0">Current and Prior Treatment</h1>
        </div>

        <div className="card-body">
          <CurrentAndPriorTreatment control={control} watch={watch} setValue={setValue} getValues={getValues} resetField={resetField} />
        </div>
      </div>
    </div>
  );
};

export default CaseStepOne;
